import { useState, useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';
import Emarsys from '../Emarsys';
import styled from './index.module.scss';
import { emailShareMap, emailReceiveMap } from '@/constants/referral';
import EfModal from '@/components/UI/Modal';
import Klaviyo from '@/components/SignUpForm/Klaviyo';
import KlaviyoView from '@/components/SignUpForm/Klaviyo/BaseFormView';
import Mailchimp from '@/components/SignUpForm/Mailchimp';
import MailchimpView from '@/components/SignUpForm/Mailchimp/BaseFormView';
import { EmailIcon, SubSuccessIcon } from '@/components/UI/svg/Referral';
import { useGlobal } from '@/utils/useGlobal';
import { getPrivacyPolicy } from '@/utils/common';

export type ISubscribeHandle = {
    handleOpen: () => void;
};

interface ISubscribeProp {
    isShare?: boolean;
}

const Subscribe = forwardRef<ISubscribeHandle, ISubscribeProp>(({ isShare = false }, ref) => {
    const { useLang, useRouter, gtm, Link } = useGlobal();
    const { t } = useLang('referral');
    const { locale } = useRouter();
    const [showIcon, setShowIcon] = useState(false);
    const [open, setOpen] = useState(false);
    const emailMap = isShare ? emailShareMap : emailReceiveMap;
    const emailConf = emailMap[locale] || emailMap.eu;
    const [step, setStep] = useState(1); // 1 订阅 2 订阅成功

    let interObserver: IntersectionObserver | null;
    useEffect(() => {
        const apperCb: IntersectionObserverCallback = (params: IntersectionObserverEntry[]) => {
            const data = params[0];
            const calIconStatus = data.intersectionRatio <= 0;
            setShowIcon(calIconStatus);
        };
        interObserver && interObserver.disconnect();
        interObserver = new IntersectionObserver(apperCb);

        const chooseBtn = document.querySelector('#referral-banner');
        if (chooseBtn) {
            interObserver.observe(chooseBtn as HTMLElement);
        }

        return () => {
            interObserver && interObserver.disconnect();
        };
    }, []);

    useImperativeHandle(ref, () => ({
        handleOpen: () => {
            setOpen(true);
        },
    }));

    const successCallBack = () => {
        setStep(2);
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'activity_join',
                tag: 'None',
                button_name: t('subscribeReceive.btnText'),
                status: 'success',
            });
        } catch (e) {
            console.error(e);
        }
    };

    const errorCallback = () => {
        gtm.push({
            event: 'uaEvent',
            event_name: 'activity_join',
            button_name: t('subscribeReceive.btnText'),
            status: 'fail',
        });
    };

    const createdEmail = useMemo(() => {
        if (emailConf.type === 'emarsys') {
            return (
                <Emarsys
                    source={emailConf.source}
                    submitText={t('subscribeReceive.btnText')}
                    placeholder={t('subscribeReceive.placeholder')}
                    successCallback={successCallBack}
                    errorCallback={errorCallback}
                />
            );
        }
        if (emailConf.type === 'mailchimp') {
            return (
                <Mailchimp successCallback={successCallBack} errorCallback={errorCallback}>
                    <MailchimpView
                        action={emailConf.action}
                        extraFields={emailConf.extraFields}
                        submitText={t('subscribeReceive.btnText')}
                        placeholder={t('subscribeReceive.placeholder')}
                    />
                </Mailchimp>
            );
        }
        return (
            <Klaviyo
                extra_properties={{
                    custom_source: emailConf.source,
                    $source: emailConf.source,
                    $method_type: 'Klaviyo Form',
                    $consent_version: 'Embed default text',
                    $consent_form_id: emailConf.consent_form_id,
                }}
                successCallback={successCallBack}
                errorCallback={errorCallback}
            >
                <KlaviyoView list_id={emailConf.list_id} submitText={t('subscribeReceive.btnText')} placeholder={t('subscribeReceive.placeholder')} />
            </Klaviyo>
        );
    }, [emailConf]);

    return (
        <>
            {isShare && (
                <div
                    style={{ display: showIcon ? 'block' : 'none', opacity: showIcon ? 1 : 0 }}
                    className={styled.subscribe}
                    onClick={() => {
                        gtm.push({
                            event: 'uaEvent',
                            event_name: 'floating_button',
                            button_name: 'Subsription',
                        });
                        setOpen(true);
                    }}
                >
                    <EmailIcon />
                </div>
            )}
            {/* 订阅弹窗 */}
            <EfModal
                className={`${styled.subscribe_modal} ${step === 2 ? 'success' : ''}`}
                open={open}
                visible={open}
                width={step === 1 ? 560 : 450}
                onCancel={() => {
                    setOpen(false);
                    setStep(1);
                }}
                centered
            >
                <div className="content">
                    <div className="icon">{step === 1 ? <EmailIcon /> : <SubSuccessIcon />}</div>
                    {step === 1 ? (
                        <>
                            <h6 className="title">{t('subscribeReceive.title')}</h6>
                            <p className="subtitle">{t('subscribeReceive.subtitle')}</p>
                            {createdEmail}
                            <p className="protocol">
                                {t.rich('subscribeReceive.protocol', {
                                    line1: (text: string) => (
                                        <Link
                                            className="link"
                                            href={
                                                locale === 'jp'
                                                    ? 'https://account.ecoflow.com/agreement/ja-jp/PrivacyPolicy.html'
                                                    : `https://${locale}.ecoflow.com/policies/terms-of-service`
                                            }
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {text}
                                        </Link>
                                    ),
                                    line2: (text: string) => (
                                        <Link
                                            className="link"
                                            href={locale === 'jp' ? 'https://account.ecoflow.com/agreement/ja-jp/TermsOfUse.html' : getPrivacyPolicy(locale)}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {text}
                                        </Link>
                                    ),
                                })}
                            </p>
                        </>
                    ) : (
                        <h6 className="title">{t('subscribeReceive.success_title')}</h6>
                    )}
                </div>
            </EfModal>
        </>
    );
});

export default Subscribe;
